import { useHistory } from 'react-router-dom';
import { useState, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { CboRole } from '@cbo/shared-library';
import { useQueryClient } from '@tanstack/react-query';
import { dialogTitleSx, dialogActionsSx, createDialogSx, closeButtonSx, loadingSx } from '../styles';
import SelectUsers from './SelectUsers';
import UnsavedFormDataPrompt from '../../../components/UnsavedDataPrompt/UnsavedFormDataPrompt';
import { useOrganizationUsersQuery, useGetCustomReportSharesQuery } from '../../requests/queries';
import {
  usePublishReportMutation,
  useUpdatePublishReportMutation,
  useUnpublishReportMutation,
} from '../../requests/mutations';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import ActionsDialog from '../../../components/ActionsDialog/ActionsDialog';
import { ReportShareType, UpdatePublishReportData, CustomReportsViewerRole } from '../../requests/index';
import { formatSelection } from '../utils/PublishReportUtils';
import queryKeys from '../../requests/queryKeys';

const centralReportsKey = 'admin.centralReportsPage';
const publishReportKey = `${centralReportsKey}.publishReport`;
const managePublishReportKey = `${centralReportsKey}.managePublishReport`;
const unPublishReportKey = `${centralReportsKey}.unpublishReport`;
export interface SelectedUser {
  groupType?: string; // 'roles' or 'individuals' group
  optionId: string;
  name: string;
  id: string;
  userName?: string;
}

interface CentralReportsFormType {
  selectedUsers: SelectedUser[];
}

// Payload structure for API
interface PublishReportPayload {
  reportId: number;
  userIds?: string[];
  userRoles?: CustomReportsViewerRole[];
}

// Mode enum for publish dialog
export enum PublishMode {
  PUBLISH = 'publish',
  MANAGE = 'manage',
}
interface PublishReportDialogProps {
  open: boolean;
  handleClose: () => void;
  reportId: number;
  reportName: string;
  onPublishSuccess?: () => void;
  mode?: PublishMode; // determine if it's publish or manage mode
}

// Role labels mapping
const roleLabels: Partial<Record<CustomReportsViewerRole, string>> = {
  [CboRole.ASSISTANT_MANAGER_LINK_VIEWER]: 'Assistant Manager',
  [CboRole.ABOVE_STORE_MANAGER_LINK_VIEWER]: 'Above Store Manager',
  [CboRole.STORE_MANAGER_LINK_VIEWER]: 'Restaurant manager',
  [CboRole.GENERAL_MANAGER_LINK_VIEWER]: 'General Manager',
  [CboRole.ACCOUNTANT_LINK_VIEWER]: 'Accountant/Bookkeeper',
};

export enum UserGroupType {
  INDIVIDUALS = 'individuals',
  ROLES = 'roles',
}

const { INDIVIDUALS, ROLES } = UserGroupType;
const { DIRECT, ROLE_BASED } = ReportShareType;

// Component to display user with formatted displayName and email
function UserDisplayLabel({ displayName, email }: { displayName: string; email: string }) {
  const theme = useTheme();
  return (
    <>
      <Typography color={theme.palette.text.primary}>{displayName}</Typography>
      <Typography color={theme.palette.text.secondary}>{email}</Typography>
    </>
  );
}

// Utility: extract userIds and userRoles from selected users
const extractUserbyIdsAndRoles = (users: SelectedUser[]) => {
  const userIds = users.filter((item) => item.groupType === UserGroupType.INDIVIDUALS).map((item) => item.optionId);
  const roles = users
    .filter((item) => item.groupType === UserGroupType.ROLES)
    .map((item) => item.optionId) as CustomReportsViewerRole[];
  return { userIds, roles };
};

// Helper to check whether two selection lists contain the same items (by id)
const haveSameSelections = (a: SelectedUser[], b: SelectedUser[]) => {
  if (a.length !== b.length) return false;
  const bIds = new Set(b.map((item) => item.id));
  return a.every((item) => bIds.has(item.id));
};

/* Main dialog component for publishing a report */
function PublishReportDialog({
  open,
  handleClose: closeDialog,
  reportId,
  reportName,
  onPublishSuccess,
  mode = PublishMode.PUBLISH,
}: Readonly<PublishReportDialogProps>) {
  const { t } = useTranslation();
  const history = useHistory();
  const publishReportMutation = usePublishReportMutation();
  const updatePublishReportMutation = useUpdatePublishReportMutation();
  const unpublishReportMutation = useUnpublishReportMutation();
  const { setSnackbarState } = useSnackbar();
  const queryClient = useQueryClient();

  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);
  const [uncheckConfirmOpen, setUncheckConfirmOpen] = useState(false);
  const [unpublishConfirmOpen, setUnpublishConfirmOpen] = useState(false);
  const [pendingUncheck, setPendingUncheck] = useState<{
    groupId: string;
    optionId: string;
    optionLabel: string;
    userName?: string;
  } | null>(null);
  const [currentReportId, setCurrentReportId] = useState<number>(reportId);
  const [currentReportName, setCurrentReportName] = useState<string>(reportName);
  const [initialSelectedUsers, setInitialSelectedUsers] = useState<SelectedUser[]>([]);
  const bypassNavigationRef = useRef(false);
  const isManageMode = mode === PublishMode.MANAGE;
  const { data: organizationUsers = [], isLoading: isLoadingUsers } = useOrganizationUsersQuery(open);

  // Fetch published report details when in manage mode
  const shouldFetchShares = isManageMode && open && !!reportId;
  const { data: publishedReportDetails, isLoading: isLoadingPublishedDetails } = useGetCustomReportSharesQuery(
    reportId,
    shouldFetchShares
  );

  const formContext = useForm<CentralReportsFormType>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      selectedUsers: [],
    },
  });

  // Destructure form helpers early so helpers below can use them
  const { control, setValue, getValues, reset } = formContext;
  const selectedUsers = useWatch({ control, name: 'selectedUsers' });

  const hasAnyInitialSelection = (users: SelectedUser[]) => {
    const initialSelections = users.some((item) => initialSelectedUsers.some((initial) => initial.id === item.id));
    return initialSelections;
  };

  // Update current report id/name when dialog opens or props change
  useEffect(() => {
    if (!open) return;
    setCurrentReportId(reportId);
    setCurrentReportName(reportName);
  }, [open, reportId, reportName]);

  // Build initial selected users from publishedReportDetails when in manage mode
  useEffect(() => {
    if (!open || !isManageMode) return;

    const shares = publishedReportDetails ?? [];

    if (shares.length === 0) {
      // Only clear if not already empty to avoid triggering redundant updates
      if ((getValues && getValues('selectedUsers')?.length) || initialSelectedUsers.length) {
        setValue('selectedUsers', []);
        setInitialSelectedUsers([]);
      }
      return;
    }

    const initialUsers: SelectedUser[] = [];

    // Process each share record
    shares.forEach((share) => {
      if (share.shareType === DIRECT && share.sharedWithUserId) {
        // Individual user share
        const user = organizationUsers.find((u) => u.username === share.sharedWithUserId);
        initialUsers.push(
          formatSelection(INDIVIDUALS, share.sharedWithUserId, user?.fullName ?? share.sharedWithUserId, user?.email)
        );
      } else if (share.shareType === ROLE_BASED && share.sharedWithRoles && share.sharedWithRoles.length > 0) {
        // Role-based shares
        share.sharedWithRoles.forEach((role) => {
          const roleLabel = roleLabels[role] ?? role;
          initialUsers.push(formatSelection(ROLES, role, roleLabel));
        });
      }
    });

    // Avoid updating state if the computed initialUsers are the same as current values
    const currentFormUsers = getValues('selectedUsers') ?? [];
    const sameAsInitialState = haveSameSelections(initialUsers, initialSelectedUsers);
    const sameAsFormState = haveSameSelections(initialUsers, currentFormUsers);

    if (sameAsInitialState && sameAsFormState) return;

    setValue('selectedUsers', initialUsers);
    setInitialSelectedUsers(initialUsers);
  }, [open, isManageMode, publishedReportDetails, organizationUsers, setValue, getValues, initialSelectedUsers]);

  const userRoles = useMemo(
    () =>
      Object.entries(roleLabels).map(([role, label]) => ({
        id: role as CustomReportsViewerRole,
        label,
      })),
    []
  );

  // Structure user options for the SelectUsers component
  const userOptions = useMemo(
    () => [
      {
        id: INDIVIDUALS,
        label: t(`${publishReportKey}.individuals`),
        options: organizationUsers.map((user) => ({
          id: user.username,
          label: <UserDisplayLabel displayName={user.fullName} email={user.email} />,
          userName: user.email,
          displayName: user.fullName,
        })),
      },
      {
        id: ROLES,
        label: t(`${publishReportKey}.userRoles`),
        options: userRoles,
      },
    ],
    [organizationUsers, userRoles, t]
  );

  const resetUnpublishState = () => {
    setUnpublishConfirmOpen(false);
    setPendingUncheck(null);
  };

  const handleClose = () => {
    reset();
    setInitialSelectedUsers([]);
    setUncheckConfirmOpen(false);
    resetUnpublishState();
    closeDialog();
  };

  const invalidateCustomReportsSharesQuery = () => {
    const sharesKey = queryKeys.customReports.reportShareDetails(reportId ?? 0);
    queryClient.invalidateQueries({ queryKey: sharesKey });
  };

  // Common finalization after a successful mutation
  const finalizeSuccess = () => {
    resetUnpublishState();
    bypassNavigationRef.current = true;
    handleClose();
    invalidateCustomReportsSharesQuery();
    if (onPublishSuccess) onPublishSuccess();
  };

  // set selected options
  const setSelectedOptions = (opts: SelectedUser[]) => setValue('selectedUsers', opts);

  const getSelectedUsers = (): SelectedUser[] => getValues('selectedUsers') ?? [];

  const isInitiallySelectedById = (id: string) => initialSelectedUsers.some((item) => item.id === id);

  const removeSelectionById = (id: string) => {
    const current = getSelectedUsers();
    setValue(
      'selectedUsers',
      current.filter((item) => item.id !== id)
    );
  };

  const toggleSelection = (formatted: SelectedUser) => {
    const current = getSelectedUsers();
    const exists = current.some((item) => item.id === formatted.id);
    const newSelected = exists ? current.filter((item) => item.id !== formatted.id) : [...current, formatted];
    setValue('selectedUsers', newSelected);
    return { exists, newSelected };
  };

  // Custom handler for unchecking in manage mode with confirmation
  const handleOptionToggle = (groupId: string, optionId: string, optionLabel: string, userName?: string) => {
    const formatted = formatSelection(groupId, optionId, optionLabel, userName);

    const currentSelected = getSelectedUsers();
    const exists = currentSelected.some((item) => item.id === formatted.id);

    // Check if this option was in the initial selections (from database)
    const wasInitiallySelected = isInitiallySelectedById(formatted.id);

    // If unchecking in manage mode AND it was originally selected from database, show confirmation
    if (exists && isManageMode && wasInitiallySelected) {
      // Check if this is the last option
      const newSelectedAfterRemoval = currentSelected.filter((item) => item.id !== formatted.id);

      if (newSelectedAfterRemoval.length === 0) {
        // Last option - show unpublish confirmation directly
        setPendingUncheck({ groupId, optionId, optionLabel, userName });
        setUnpublishConfirmOpen(true);
      } else {
        // Not the last option - show uncheck confirmation
        setPendingUncheck({ groupId, optionId, optionLabel, userName });
        setUncheckConfirmOpen(true);
      }
    } else {
      // Normal toggle behavior for publish mode, checking, or removing newly added selections
      const { newSelected } = toggleSelection(formatted);

      // Check if all options are deselected in manage mode
      if (isManageMode && newSelected.length === 0) {
        setUnpublishConfirmOpen(true);
      }
    }
  };

  const handleUncheckCancel = () => {
    setUncheckConfirmOpen(false);
    setPendingUncheck(null);
  };

  const handleUncheckConfirm = () => {
    if (pendingUncheck) {
      const formattedId = `${pendingUncheck.groupId}.${pendingUncheck.optionId}`;

      removeSelectionById(formattedId);

      const newSelected = getSelectedUsers();

      // Check if all options are now deselected
      if (newSelected.length === 0) {
        setUncheckConfirmOpen(false);
        setUnpublishConfirmOpen(true);
      } else {
        handleUncheckCancel();
      }
    }
  };

  // Custom handler for Select All toggle in manage mode
  const handleSelectAllToggle = (
    groupId: string,
    allIds: { id: string; name: string; userName?: string }[],
    isCurrentlyAllSelected: boolean
  ) => {
    const currentSelected = selectedUsers;

    if (isCurrentlyAllSelected && isManageMode) {
      // User is deselecting all - check if any were from initial selections
      const itemsToDeselect = currentSelected.filter((item) => item.groupType === groupId);
      const hasInitialSelections = hasAnyInitialSelection(itemsToDeselect);

      if (hasInitialSelections) {
        // Check if deselecting this group will leave no selections
        const otherGroupSelections = currentSelected.filter((item) => item.groupType !== groupId);

        if (otherGroupSelections.length === 0) {
          // Will result in no selections - show unpublish confirmation
          setUnpublishConfirmOpen(true);
          return;
        }
      }
    }

    // Default toggle behavior
    if (isCurrentlyAllSelected) {
      // Deselect all from this specific group only
      setSelectedOptions(currentSelected.filter((item) => item.groupType !== groupId));
    } else {
      const all = allIds.map((option) => formatSelection(groupId, option.id, option.name, option.userName));
      // Remove existing selections from this group and add the new ones
      const otherGroupSelections = currentSelected.filter((item) => item.groupType !== groupId);
      setSelectedOptions([...otherGroupSelections, ...all]);
    }
  };

  const handleClearAll = () => {
    const currentSelected = selectedUsers;

    // Check if any selections were initially published
    const hasInitiallyPublished = hasAnyInitialSelection(currentSelected);

    if (hasInitiallyPublished && currentSelected.length > 0) {
      // Show unpublish confirmation
      setUnpublishConfirmOpen(true);
    } else {
      // No initially published items, safe to clear
      setValue('selectedUsers', []);
    }
  };

  // Prepare payload from selected users and roles
  const preparePublishPayload = (): PublishReportPayload => {
    const { userIds: selectedUserIds, roles: selectedUserRoles } = extractUserbyIdsAndRoles(selectedUsers);

    return {
      reportId: currentReportId,
      userIds: selectedUserIds,
      userRoles: selectedUserRoles,
    };
  };

  // Compute additions and removals of userIds and roles
  const diffAddRemove = <T,>(current: T[], initial: T[]) => {
    const initialSet = new Set(initial);
    const currentSet = new Set(current);
    const add = current.filter((x) => !initialSet.has(x));
    const remove = initial.filter((x) => !currentSet.has(x));
    return { add, remove };
  };

  // Prepare update payload for manage mode by calculating differences
  const prepareUpdatePublishPayload = (): UpdatePublishReportData => {
    const currentSelectedUsers = selectedUsers;

    // Extract current and initial user IDs and roles using utility function
    const { userIds: currentUserIds, roles: currentUserRoles } = extractUserbyIdsAndRoles(currentSelectedUsers);
    const { userIds: initialUserIds, roles: initialUserRoles } = extractUserbyIdsAndRoles(initialSelectedUsers);

    const { add: addUserIds, remove: removeUserIds } = diffAddRemove(currentUserIds, initialUserIds);
    const { add: addUserRoles, remove: removeUserRoles } = diffAddRemove(currentUserRoles, initialUserRoles);

    return {
      reportId: currentReportId,
      addUserIds: addUserIds.length > 0 ? addUserIds : undefined,
      removeUserIds: removeUserIds.length > 0 ? removeUserIds : undefined,
      addUserRoles: addUserRoles.length > 0 ? addUserRoles : undefined,
      removeUserRoles: removeUserRoles.length > 0 ? removeUserRoles : undefined,
    };
  };

  // Handle publishing a new report
  const handlePublishReport = async () => {
    // Validate that reportId exists before performing any operations
    if (!currentReportId) {
      setSnackbarState({
        open: true,
        message: t(`${publishReportKey}.missingReportId`, { reportName: currentReportName }),
        color: 'error',
      });
      return;
    }

    try {
      const payload = preparePublishPayload();
      await publishReportMutation.mutateAsync({ publishReportData: payload });

      // Finalize success flow
      finalizeSuccess();

      // Show success notification
      setSnackbarState({
        open: true,
        message: t(`${publishReportKey}.success`),
        color: 'success',
      });
    } catch (error) {
      // Show error notification
      setSnackbarState({
        open: true,
        message: t(`${publishReportKey}.error`),
        color: 'error',
      });
    }
  };

  // Handle updating an already published report
  const handleManagePublishReport = async () => {
    // Validate that reportId exists before performing any operations
    if (!currentReportId) {
      setSnackbarState({
        open: true,
        message: t(`${publishReportKey}.missingReportId`, { reportName: currentReportName }),
        color: 'error',
      });
      return;
    }

    try {
      const updatePayload = prepareUpdatePublishPayload();
      await updatePublishReportMutation.mutateAsync({ updatePublishReportData: updatePayload });

      // Finalize success flow
      finalizeSuccess();

      // Show success notification
      setSnackbarState({
        open: true,
        message: t(`${managePublishReportKey}.success`),
        color: 'success',
      });
    } catch (error) {
      // Show error notification
      setSnackbarState({
        open: true,
        message: t(`${managePublishReportKey}.error`),
        color: 'error',
      });
    }
  };

  // Wrapper to call the appropriate handler based on mode
  const handleSubmit = async () => {
    if (isManageMode) {
      await handleManagePublishReport();
    } else {
      await handlePublishReport();
    }
  };

  const handleUnpublishAction = async () => {
    if (!currentReportId) return;

    try {
      await unpublishReportMutation.mutateAsync({ reportId: currentReportId });

      // Finalize success flow
      finalizeSuccess();

      // Show success notification
      setSnackbarState({
        open: true,
        message: t(`${unPublishReportKey}.success`),
        color: 'success',
      });
    } catch (error) {
      // Show error notification
      setSnackbarState({
        open: true,
        message: t(`${unPublishReportKey}.error`),
        color: 'error',
      });
    }
  };

  // selected users from form

  // Check if current selections differ from initial selections
  const hasChangesFromInitial = useMemo(() => {
    if (mode === PublishMode.PUBLISH) return selectedUsers.length > 0;
    return !haveSameSelections(selectedUsers, initialSelectedUsers);
  }, [selectedUsers, initialSelectedUsers, mode]);

  const hasUnsavedChanges = hasChangesFromInitial;

  const noSelection = selectedUsers.length === 0;
  const missingReportId = !currentReportId;
  const noChangesInManageMode = isManageMode && !hasChangesFromInitial;

  const isPublishDisabled = [noSelection, missingReportId, noChangesInManageMode].some(Boolean);

  const isMutationLoading = [
    publishReportMutation?.isLoading,
    updatePublishReportMutation?.isLoading,
    unpublishReportMutation?.isLoading,
  ].some(Boolean);

  const handleChipRemoval = (itemId: string) => {
    const item = selectedUsers.find((opt) => opt.id === itemId);
    if (!item?.groupType) return;

    // Delegate to existing handleOptionToggle which has warning logic
    handleOptionToggle(item.groupType, item.optionId, item.name, item.userName);
  };

  const handleUnsavedPromptConfirm = () => {
    setUnsavedPromptOpen(false);
    handleClose();
  };

  const handleCloseClick = () => {
    if (hasUnsavedChanges) {
      setUnsavedPromptOpen(true);
    } else {
      handleClose();
    }
  };

  const titleKey = isManageMode ? `${managePublishReportKey}.title` : `${publishReportKey}.publishDialogTitle`;
  const contentKey = isManageMode ? `${managePublishReportKey}.content` : `${publishReportKey}.publishDialogContent`;
  const saveKey = isManageMode ? `${managePublishReportKey}.save` : `${publishReportKey}.savePublish`;

  const dialogTitle = t(titleKey, { reportName: currentReportName });
  const dialogContentText = t(contentKey);
  const saveButtonText = t(saveKey);

  const waitingForPublishedDetails = isManageMode && isLoadingPublishedDetails;
  const isPageLoading = isLoadingUsers || waitingForPublishedDetails;

  // Consolidate manage-mode handlers to avoid repeated ternary expressions in JSX
  const manageHandlers = isManageMode
    ? {
        onOptionToggle: handleOptionToggle,
        onSelectAllToggle: handleSelectAllToggle,
        onRemoveItem: handleChipRemoval,
        onClearAll: handleClearAll,
      }
    : {};

  return (
    <>
      <UnsavedFormDataPrompt
        textTranslationKey='unsavedDataDialog'
        when={hasUnsavedChanges}
        open={unsavedPromptOpen}
        navigate={(path) => history.push(path)}
        shouldBlockNavigation={() => !bypassNavigationRef.current}
        onCancel={() => setUnsavedPromptOpen(false)}
        onConfirm={handleUnsavedPromptConfirm}
      />
      {/* Confirmation dialog for unchecking an option in manage mode */}
      <ActionsDialog
        open={uncheckConfirmOpen}
        dataTestId='uncheck-confirmation-dialog'
        dialogTitle={t(`${managePublishReportKey}.uncheckConfirmTitle`)}
        dialogContentText={t(`${managePublishReportKey}.uncheckConfirmContent`, {
          name: pendingUncheck?.optionLabel ?? '',
        })}
        closeButtonText={t('buttonText.cancel')}
        closeButtonVariant='text'
        actionButtonText={t(`${managePublishReportKey}.uncheckConfirmTitle`)}
        actionButtonColor='error'
        onClose={handleUncheckCancel}
        onActionClick={handleUncheckConfirm}
      />
      {/* Confirmation dialog for unpublishing when all options are deselected */}
      <ActionsDialog
        open={unpublishConfirmOpen}
        dataTestId='unpublish-confirmation-dialog'
        dialogTitle={t(`${managePublishReportKey}.unpublishConfirmTitle`, {
          reportName: currentReportName,
        })}
        dialogContentText={t(`${managePublishReportKey}.unpublishConfirmContent`)}
        closeButtonText={t('buttonText.cancel')}
        closeButtonVariant='text'
        actionButtonText={t(`${unPublishReportKey}.confirm`)}
        actionButtonColor='error'
        onClose={resetUnpublishState}
        onActionClick={handleUnpublishAction}
        disableActionButton={unpublishReportMutation.isLoading}
      />
      <Dialog
        open={open}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleClose();
        }}
        TransitionProps={{
          onExited: () => {
            setValue('selectedUsers', []);
            setInitialSelectedUsers([]);
            bypassNavigationRef.current = false;
          },
        }}
        PaperProps={{
          sx: createDialogSx,
        }}
      >
        <DialogTitle sx={dialogTitleSx}>{dialogTitle}</DialogTitle>
        <IconButton onClick={handleCloseClick} sx={closeButtonSx} data-testid='publish-report-close-button'>
          <CloseIcon />
        </IconButton>
        <>
          <DialogContent>
            {isPageLoading ? (
              <CircularProgress sx={loadingSx} aria-label={t('global.loading')} />
            ) : (
              <>
                <DialogContentText>{dialogContentText}</DialogContentText>
                <SelectUsers
                  selectedOptions={selectedUsers}
                  setSelectedOptions={setSelectedOptions}
                  userOptions={userOptions}
                  {...manageHandlers}
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={dialogActionsSx}>
            <Grid container justifyContent='flex-end' alignItems='center'>
              <Grid>
                <Button onClick={handleCloseClick}>{t('buttonText.cancel')}</Button>
                <LoadingButton
                  sx={{ marginLeft: '16px' }}
                  variant='contained'
                  disabled={isPublishDisabled}
                  loading={isMutationLoading}
                  onClick={handleSubmit}
                >
                  {saveButtonText}
                </LoadingButton>
              </Grid>
            </Grid>
          </DialogActions>
        </>
      </Dialog>
    </>
  );
}

export default PublishReportDialog;
